import { computed, defineComponent, onBeforeUnmount, onMounted, ref, useCssModule } from '@nuxtjs/composition-api';
export default defineComponent({
    name: 'Dropdown',
    inheritAttrs: false,
    model: {
        prop: 'value',
        event: 'input',
    },
    props: {
        hasFullWidth: {
            default: false,
            type: Boolean,
        },
        isAllValueHidden: {
            default: false,
            type: Boolean,
        },
        isClearable: {
            default: false,
            type: Boolean,
        },
        name: {
            default: '',
            type: String,
        },
        items: {
            required: true,
            type: Array,
        },
        size: {
            default: 'medium',
            type: String,
            validator: (value) => {
                return ['medium', 'small'].includes(value);
            },
        },
        tabSize: {
            default: 'small',
            type: String,
            validator: (value) => {
                return ['small', 'medium', 'big'].includes(value);
            },
        },
        fontSize: {
            default: 'small',
            type: String,
            validator: (value) => {
                return ['small', 'medium', 'big'].includes(value);
            },
        },
        title: {
            default: '',
            type: [String, Object],
            validator: (value) => {
                return (typeof value === 'string' ||
                    (toString.call(value) === '[object Object]' &&
                        (typeof value?.tab === 'string' || typeof value?.tooltip === 'string')));
            },
        },
        theme: {
            default: 'light',
            type: String,
            validator: (value) => {
                return ['dark', 'light'].includes(value);
            },
        },
        value: {
            default: '',
            type: String,
        },
        resetValue: {
            default: '',
            type: String,
        },
        withBlackoutFullHeight: {
            default: false,
            type: Boolean,
        },
        withGapOnMobileTooltip: {
            default: false,
            type: Boolean,
        },
    },
    emits: ['input'],
    setup(props, { emit }) {
        /**
         * CSS Modules.
         */
        const css = useCssModule();
        const rootEl = ref();
        const isOpened = ref(false);
        const currentName = ref(props.name);
        const currentItems = computed(() => {
            return props.items.map((option) => {
                if (typeof option === 'string') {
                    return {
                        value: option,
                        title: option,
                    };
                }
                return {
                    value: option.value,
                    title: option.title || option.value,
                };
            });
        });
        const currentItem = computed(() => {
            return currentItems.value.find((item) => props.value === item.value) || currentItems.value[0];
        });
        const isScrollable = computed(() => props.items?.length > 6);
        function handleBlackout() {
            isOpened.value = false;
        }
        function handleChange(event) {
            const target = event.target;
            const { value = '' } = target;
            isOpened.value = false;
            emit('input', value);
        }
        function handleClick() {
            isOpened.value = !isOpened.value;
        }
        function handleClickOutside({ target }) {
            if (rootEl.value && !rootEl.value.contains(target)) {
                isOpened.value = false;
            }
        }
        function handleKeyup({ code = '' }) {
            if (code === 'Escape') {
                isOpened.value = false;
            }
        }
        function handleReset() {
            emit('input', props.resetValue || currentItems.value[0].value);
            isOpened.value = false;
        }
        onBeforeUnmount(() => {
            window.removeEventListener('click', handleClickOutside);
            window.removeEventListener('keyup', handleKeyup);
        });
        onMounted(() => {
            if (!props.name) {
                const id = (Math.random() * 1000000).toFixed(0);
                currentName.value = `dropdown-${id}`;
            }
            window.addEventListener('click', handleClickOutside);
            window.addEventListener('keyup', handleKeyup);
        });
        return {
            css,
            currentItems,
            currentItem,
            currentName,
            isOpened,
            isScrollable,
            handleBlackout,
            handleChange,
            handleClick,
            handleReset,
            rootEl,
        };
    },
});
